import React, { useMemo } from "react";
import { Menu, Avatar, Typography } from "antd";
import {
  AppstoreAddOutlined,
  ApartmentOutlined,
  BankOutlined,
  BookOutlined,
  CustomerServiceOutlined,
  DashboardOutlined,
  FileTextOutlined,
  FormOutlined,
  IdcardOutlined,
  MessageOutlined,
  PlayCircleOutlined,
  SolutionOutlined,
  TeamOutlined,
  UserOutlined,
  UsergroupAddOutlined,
} from "@ant-design/icons";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../hooks";

import "./AdminMenu.scss";

function getRole(authUser) {
  return authUser?.user?.role || authUser?.role || null;
}

function getUserVM(authUser, t) {
  const u = authUser?.user || authUser || {};
  const firstname = u.firstname || "";
  const lastname = u.lastname || "";
  const avatar = u.avatar || u.image || null;
  return {
    fullName: `${firstname} ${lastname}`.trim() || t("roles.user"),
    avatar,
  };
}

/**
 * El menu guarda la CLAVE, no el texto.
 *
 * `roleRoutes` es una constante de modulo: se evalua UNA vez, al importar el
 * fichero. Si aqui hubiera texto ya traducido, el menu se quedaria congelado en
 * el idioma que hubiera al arrancar y no cambiaria al cambiar de idioma. La
 * traduccion ocurre en el render, que es donde el idioma esta vivo.
 */
const roleRoutes = {
  admin: [
    { path: "/portal/users", key: "nav.admin.users", icon: <UserOutlined /> },
    {
      path: "/portal/register",
      key: "nav.admin.profile",
      icon: <IdcardOutlined />,
    },
    {
      path: "/portal/institution",
      key: "nav.admin.institution",
      icon: <BankOutlined />,
    },
  ],
  partner: [
    {
      path: "/portal/partner/register",
      key: "nav.partnerInfo",
      icon: <SolutionOutlined />,
    },
    {
      path: "/portal/partner/institution",
      key: "nav.institutions",
      icon: <ApartmentOutlined />,
    },
    {
      path: "/portal/partner/facturation",
      key: "nav.billing",
      icon: <FileTextOutlined />,
    },
    {
      path: "/portal/partner/support",
      key: "nav.support",
      icon: <CustomerServiceOutlined />,
    },
  ],
  institution: [
    {
      path: "/portal/inst/profile",
      key: "nav.institution",
      icon: <BankOutlined />,
    },
    {
      path: "/portal/inst/therapists",
      key: "nav.therapists",
      icon: <TeamOutlined />,
    },
    {
      path: "/portal/inst/patients",
      key: "nav.users",
      icon: <UsergroupAddOutlined />,
    },
    {
      path: "/portal/inst/therapies",
      key: "nav.therapies",
      icon: <AppstoreAddOutlined />,
    },
    {
      path: "/portal/inst/metricas",
      key: "nav.metrics",
      icon: <DashboardOutlined />,
    },
    {
      path: "/portal/inst/documentacion",
      key: "nav.training",
      icon: <BookOutlined />,
    },
  ],
  therapist: [
    {
      path: "/portal/therapist/salas",
      key: "nav.createTherapies",
      icon: <AppstoreAddOutlined />,
    },
    {
      path: "/portal/therapist/evaluacion",
      key: "nav.createTests",
      icon: <FormOutlined />,
    },
    {
      path: "/portal/therapist/terapia",
      key: "nav.runTherapy",
      icon: <PlayCircleOutlined />,
    },
    {
      path: "/portal/therapist/pacientes",
      key: "nav.viewUsers",
      icon: <TeamOutlined />,
    },
    {
      path: "/portal/therapist/dashboard",
      key: "nav.viewMetrics",
      icon: <DashboardOutlined />,
    },
    {
      path: "/portal/therapist/documentacion",
      key: "nav.training",
      icon: <BookOutlined />,
    },
  ],
};

// ✅ fallback estable (NO crear [] en cada render)
const EMPTY_ROUTES = [];

function bestMatchKey(pathname, keys) {
  if (!keys?.length) return "";
  const match = keys
    .filter((k) => pathname === k || pathname.startsWith(`${k}/`))
    .sort((a, b) => b.length - a.length)[0];
  return match || "";
}

/**
 * `onAsistente` solo llega desde el cajon del movil: alli el asistente deja de
 * ser un boton flotante en la esquina del pulgar y pasa a ser la ultima entrada
 * del menu. Sin esa prop, este componente pinta exactamente lo de siempre.
 */
export function AdminMenu({ collapsed = false, onAsistente }) {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { t } = useTranslation("common");

  const role = getRole(user);
  const userVM = getUserVM(user, t);

  // ✅ memoiza routes para que NO cambie la referencia en cada render
  const routes = useMemo(() => {
    return roleRoutes[role] || EMPTY_ROUTES;
  }, [role]);

  const routeKeys = useMemo(() => routes.map((r) => r.path), [routes]);

  const menuItems = useMemo(() => {
    return routes.map((r) => ({
      key: r.path,
      icon: r.icon || <UserOutlined />,
      label: <Link to={r.path}>{t(r.key)}</Link>,
    }));
  }, [routes, t]);

  const selectedKey = useMemo(() => {
    return bestMatchKey(pathname, routeKeys);
  }, [pathname, routeKeys]);

  return (
    <div className="admin-menu">
      <Menu
        className="admin-menu__nav"
        mode="inline"
        theme="dark"
        inlineCollapsed={collapsed}
        selectedKeys={selectedKey ? [selectedKey] : []}
        items={menuItems}
      />

      {onAsistente && (
        <button
          type="button"
          className="admin-menu__asistente"
          onClick={onAsistente}
        >
          <MessageOutlined className="admin-menu__asistenteIcono" />
          <span>{t("assistant.titulo", { ns: "misc" })}</span>
        </button>
      )}

      {role === "therapist" && (
        <div
          className={`admin-menu__teacher ${collapsed ? "admin-menu__teacher--collapsed" : ""}`}
        >
          <Avatar
            size={40}
            src={userVM.avatar || undefined}
            icon={!userVM.avatar ? <UserOutlined /> : undefined}
          />
          {!collapsed && (
            <div className="admin-menu__teacherInfo">
              <Typography.Text className="admin-menu__teacherName" ellipsis>
                {userVM.fullName}
              </Typography.Text>
              <Typography.Text
                className="admin-menu__teacherRole"
                type="secondary"
              >
                {t("roles.therapist")}
              </Typography.Text>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
